import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import PageHero from "../components/blocks/PageHero";
import Section from "../components/ui/Section";
import mockData from "../mockData";
import useSEO from "../hooks/useSEO";

const Gallery = () => {
  useSEO('/gallery');
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState("all");

  const categories = ["all", "accommodations", "nature", "experiences", "dining"];

  const filteredImages = useMemo(() => {
    if (filter === "all") return mockData.gallery;
    return mockData.gallery.filter((img) => img.category === filter);
  }, [filter]);

  return (
    <div className="min-h-screen bg-[#050706] text-[#F4F1E8] pt-16">
      <PageHero
        title="Gallery"
        subtitle="A visual journey through Moonrise Valley"
        image="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=2400&q=80"
        heightClass="h-[52vh]"
      />

      <div className="sticky top-16 z-40 border-b border-white/10 bg-black/55 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap justify-center gap-3 px-4 py-4">
          {categories.map((cat) => {
            const active = filter === cat;
            return (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={
                  "rounded-full px-5 py-2 text-sm transition " +
                  (active
                    ? "bg-[#C9A227] text-[#111]"
                    : "border border-white/10 bg-white/5 text-white/75 hover:bg-white/10")
                }
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            );
          })}
        </div>
      </div>

      <Section className="py-16">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredImages.map((image) => (
            <button
              key={image.id}
              className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-[0_18px_60px_rgba(0,0,0,0.35)]"
              onClick={() => setSelectedImage(image)}
            >
              <img
                src={image.url}
                alt={image.alt}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="absolute bottom-4 left-4 right-4 text-left opacity-0 transition-all duration-300 group-hover:opacity-100">
                <div className="text-sm font-semibold text-white">{image.alt}</div>
                <div className="text-xs text-white/70 capitalize">{image.category}</div>
              </div>
            </button>
          ))}
        </div>

        {filteredImages.length === 0 ? (
          <div className="py-20 text-center text-white/70">No images in this category yet.</div>
        ) : null}

        <div className="mt-12 text-center text-sm text-white/55">
          Note: Images are representative. Views can vary with seasons and weather.
        </div>
      </Section>

      {selectedImage ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/5 p-3 text-white"
            onClick={() => setSelectedImage(null)}
          >
            <X size={22} />
          </button>
          <div className="max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.alt}
              className="max-h-[78vh] w-full rounded-2xl object-contain"
            />
            <div className="mt-4 text-center">
              <div className="text-white">{selectedImage.alt}</div>
              <div className="text-sm text-white/60 capitalize">{selectedImage.category}</div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Gallery;
